import along from "@turf/along";
import length from "@turf/length";
import { MercatorCoordinate } from "mapbox-gl";
import { cameraGroundPosition } from "./computeCameraPosition";
import { arcAltitude, legProgress, motionFloor, type LegPlan } from "./reelAltitudes";
import { interpolateAltitude, interpolateBearing, legHeadingAt } from "./sequences/legHeading";

/**
 * Fetches the map tiles a render will need before Remotion starts asking for frames.
 *
 * `TileGate` (in TikTokVideoVanilla.tsx) makes every frame wait for Mapbox `idle` before it is
 * captured, because the free camera moves every frame and nearly every frame exposes ground with no
 * cached tiles -- each one pays its own network round trip. Measured on the render box, that per-frame
 * wait is half the total render time. The camera's path is fully known before rendering starts, so
 * there is no reason to discover it one frame at a time: walk it in coarse steps (about every 15
 * frames -- close enough together that two steps rarely miss a tile between them, far enough apart that
 * the whole film needs roughly 90 `idle` waits instead of the ~1,000 a frame-by-frame gate pays for),
 * wait for `idle` at each step, and the frame-by-frame gate then mostly finds its tiles already warm.
 *
 * This module owns the plan (`planWarmupKeyframes`) and the walk (`warmTiles`) but not the map: the
 * caller raises Mapbox's `maxTileCacheSize` before calling `warmTiles` so the tiles fetched here
 * survive until the render reaches them, and calls both from the map's `load` handler.
 *
 * Deliberately has no `remotion` import and no dependency on TikTokVideoVanilla.tsx -- the latter will
 * import this module, and pulling remotion in here would make the plan un-testable outside a
 * composition for no benefit, since none of this runs inside a Sequence.
 */

export type WarmupKeyframe = {
	lng: number;
	lat: number;
	altitude: number;
	pitch: number;
	bearing: number;
	/**
	 * The frame of the film this view belongs to, absolute. Only set when the plan was told where the
	 * film's segments are (`legStarts`), and only so a tab that renders part of the film can leave out
	 * the views it will never be near (`keyframesInRange`). Walking a view does not read it.
	 */
	frame?: number;
};

/** The fewest coarse steps the intro's descent is sampled at. */
const INTRO_STEPS = 6;
/**
 * Never let two consecutive warm-up views sit more than this many zoom levels apart. A stride of
 * fifteen frames is fine while the camera cruises, but on a climb it crosses a zoom level every ten
 * frames or so, and every level crossed is a fresh set of tiles for every source -- the instrumented
 * render showed those crossings as the one thing the frame-stride warm-up still left cold.
 */
const ZOOM_STEP = 0.5;
const zoomBetween = (a: number, b: number) => Math.abs(Math.log2(Math.max(1, a) / Math.max(1, b)));
/** Coarse views a leg needs: one per `stepFrames`, or one per half zoom level of its arc, whichever is more. */
export const legWarmupSteps = (plan: Pick<LegPlan, "entry" | "peak" | "exit" | "frames">, stepFrames: number) =>
	Math.max(2, Math.ceil(plan.frames / stepFrames), Math.ceil((zoomBetween(plan.peak, plan.entry) + zoomBetween(plan.peak, plan.exit)) / ZOOM_STEP));
/** Coarse views the intro's descent needs, by the same half-zoom rule. */
export const introWarmupSteps = (introStartAltitude: number, introEndAltitude: number) =>
	Math.max(INTRO_STEPS, Math.ceil(zoomBetween(introStartAltitude, introEndAltitude) / ZOOM_STEP) + 1);
/**
 * The sources whose tiles come over the network: the basemap, terrain, satellite imagery. These are
 * what a frame can genuinely be captured without. The composition's own GeoJSON sources are not in
 * this list on purpose -- the transport marker's position and each stop's marker are rewritten every
 * frame, Mapbox re-tiles them in a worker before it will call them loaded, and waiting for that made
 * 46% of frames take the slow path on the instrumented render. A marker drawn from its previous
 * frame's tile is off by a thirtieth of a second, which no one can see; a basemap with a hole is.
 */
export const tileSourceIds = (map: any): string[] => {
	try {
		const sources = map?.getStyle?.()?.sources ?? {};
		return Object.keys(sources).filter((id) => ["vector", "raster", "raster-dem"].includes(sources[id]?.type));
	} catch {
		return [];
	}
};
/**
 * Whether the style keeps sources inside imports, where `tileSourceIds` cannot see them. Ask once per
 * map, like `tileSourceIds`, never per poll: `getStyle()` serialises every source and layer, and on
 * Mapbox Standard the whole imported style with them. Mapbox sets the import up when the style JSON
 * arrives, so the answer is already right by the map's `load`.
 */
export const hasImports = (map: any): boolean => {
	try {
		return (map?.getStyle?.()?.imports || []).length > 0;
	} catch {
		return false;
	}
};
/**
 * True when every tile source has all the tiles its current view retains.
 *
 * Mapbox Standard (the 3D map, `lightPreset`/`show3dObjects`) keeps its sources inside the style's
 * "basemap" import, and `getStyle().sources` does not list imported sources: for Standard the list
 * above is empty, this answered yes on every frame, and frames were captured before their tiles were
 * in -- black squares over Manhattan at night, a white first second on a 3D intro. For a style with
 * imports (`styleHasImports`, from `hasImports`), ask every source cache instead. That includes our
 * own GeoJSON sources, which the list leaves out on purpose, so it is the slower answer and only
 * taken where the fast one is blind.
 */
export const tilesLoaded = (map: any, sourceIds: string[], styleHasImports: boolean) =>
	(styleHasImports ? !!map.areTilesLoaded?.() : true) &&
	sourceIds.every((id) => {
		try {
			return map.isSourceLoaded(id);
		} catch {
			return true;
		}
	});

/**
 * The composition's own GeoJSON sources: the route lines, each stop's marker, the transport dot.
 * `tileSourceIds` leaves them out of the per-frame wait, and on their own they still are. But since
 * `TileGate` captures a copy of the frame at the moment it approves it (`approvedFrame.ts`), an update
 * to one of them that Mapbox has not finished in its worker shows up a frame late in that copy: the
 * dot a step behind the ink. Waiting for them on top of the tile sources halved those frames on the
 * render box (2026-10-05) at the same render speed. The 46% slow path measured on 2026-09-20 was
 * waiting for `idle`, which also waits for label placement; asking `isSourceLoaded` does not.
 */
export const geojsonSourceIds = (map: any): string[] => {
	try {
		const sources = map?.getStyle?.()?.sources ?? {};
		return Object.keys(sources).filter((id) => sources[id]?.type === "geojson");
	} catch {
		return [];
	}
};

/** True when each listed source has finished loading. A source that has gone counts as loaded. */
export const sourcesLoaded = (map: any, sourceIds: string[]) =>
	sourceIds.every((id) => {
		try {
			return map.isSourceLoaded(id);
		} catch {
			return true;
		}
	});

/** Same threshold pair PathSequence and MarkerSequence use to flatten the camera as altitude climbs. */
const PITCH_ALTITUDE_FLOOR = 2400000;
const PITCH_ALTITUDE_CEILING = 4000000;
const MAX_PITCH = 20;

/** The leg turns fully into its own heading by this fraction in -- see PathSequence's `entryPhase`. */
const HEADING_ENTRY_FRACTION = 0.33;

const isFiniteNumber = (val: any): val is number => typeof val === "number" && !isNaN(val) && isFinite(val);

/**
 * The altitude -> pitch rule every sequence shares: flat (low pitch) once the camera is high enough
 * to see a lot of ground, tilted toward `startPitch` once it is low enough to be among the buildings.
 * Copied rather than imported because it is `remotion`'s `interpolate` with clamping on both sides,
 * and importing remotion here would pull rendering machinery into a module that has to stay testable
 * and cycle-free.
 */
const pitchForAltitude = (altitude: number, startPitch: number) => {
	const t = Math.max(0, Math.min(1, (altitude - PITCH_ALTITUDE_FLOOR) / (PITCH_ALTITUDE_CEILING - PITCH_ALTITUDE_FLOOR)));
	return startPitch + (MAX_PITCH - startPitch) * t;
};

/** Fold a longitude back into [-180, 180] -- copied from PathSequence's `wrapLongitude`, not imported. */
const wrapLongitude = (lng: number) => {
	if (!isFiniteNumber(lng)) return lng;
	return ((((lng + 180) % 360) + 360) % 360) - 180;
};

/** Push a keyframe only if every field in it is a real, usable number -- degenerate input contributes nothing rather than a broken camera move. */
const pushIfValid = (frames: WarmupKeyframe[], kf: WarmupKeyframe | null | undefined) => {
	if (!kf) return;
	if (!isFiniteNumber(kf.lng) || !isFiniteNumber(kf.lat) || !isFiniteNumber(kf.altitude) || kf.altitude <= 0) return;
	if (!isFiniteNumber(kf.pitch) || !isFiniteNumber(kf.bearing)) return;
	frames.push(kf);
};

/**
 * Six keyframes descending from `introStartAltitude` toward `introEndAltitude` over the first stop,
 * log-spaced because altitude is perceived (and zoomed) logarithmically -- see `interpolateAltitude`
 * in legHeading.ts, which both IntroSequence's cousin curves and this reuse for exactly that reason.
 */
const introKeyframes = (
	lng: number,
	lat: number,
	introStartAltitude: number,
	introEndAltitude: number,
	startPitch: number,
	introFrames?: number
): WarmupKeyframe[] => {
	const frames: WarmupKeyframe[] = [];
	if (!isFiniteNumber(lng) || !isFiniteNumber(lat)) return frames;
	const steps = introWarmupSteps(introStartAltitude, introEndAltitude);
	for (let k = 0; k < steps; k++) {
		const t = steps > 1 ? k / (steps - 1) : 1;
		const altitude = interpolateAltitude(t, introStartAltitude, introEndAltitude);
		const pitch = pitchForAltitude(altitude, startPitch);
		// The sequences never park the camera over a point; they stand it back so the point sits at
		// the leading edge of the frame -- 17 km back at a 10 km, 60 degree stop. Warming the tiles
		// under the point instead of under the camera would miss the ground the frame actually shows.
		const ground = cameraGroundPosition(pitch, 0, { lng, lat }, altitude);
		// The descent is spread over the whole intro, first view on its first frame and last on its last.
		const frame = isFiniteNumber(introFrames) && introFrames > 0 ? { frame: Math.round(t * (introFrames - 1)) } : {};
		pushIfValid(frames, ground ? { lng: ground.lng, lat: ground.lat, altitude, pitch, bearing: 0, ...frame } : null);
	}
	return frames;
};

/**
 * The camera at one point along leg `i`, reproducing PathSequence's per-frame maths closely enough
 * that the same tiles get fetched: position along the leg, altitude as the arc plus the motion floor
 * (clamped to the plan's peak, same as PathSequence clamps to its own `calcAltitude`), pitch on the
 * shared altitude rule, and heading eased in from the previous leg's arrival bearing over the first
 * third of this one.
 */
const legKeyframeAt = (phase: number, reelData: any[], i: number, plan: LegPlan, pathFeature: any, routeDistance: number, startPitch: number): WarmupKeyframe | null => {
	const easedPath = legProgress(phase);
	const along_ = along(pathFeature, routeDistance * easedPath).geometry.coordinates;

	const prevPhase = plan.frames > 0 ? legProgress(phase - 1 / plan.frames) : easedPath;
	const kmThisFrame = routeDistance * Math.abs(easedPath - prevPhase);
	const altitude = Math.max(arcAltitude(phase, plan.entry, plan.peak, plan.exit), Math.min(motionFloor(kmThisFrame, startPitch), plan.peak));

	const pitch = pitchForAltitude(altitude, startPitch);

	const ownHeading = legHeadingAt(pathFeature?.geometry?.coordinates, easedPath);
	const arrivedHeading = i > 0 ? legHeadingAt(reelData?.[i - 1]?.path, 1) : null;
	// Same window PathSequence uses (`interpolate(pathPhase, [0, 0.33], [0, 1], { easing: Easing.inOut(Easing.ease) })`),
	// eased with `legProgress` -- the cubic in-out the rest of this module already shares with
	// reelAltitudes -- rather than remotion's `Easing.inOut(Easing.ease)` bezier, since remotion cannot
	// be imported here. The two curves agree at their ends and diverge only slightly in between, which
	// does not move the camera far enough to change which tiles a coarse warm-up step touches.
	const entryPhase = legProgress(phase / HEADING_ENTRY_FRACTION);
	const heading = arrivedHeading === null ? ownHeading : interpolateBearing(entryPhase, arrivedHeading, ownHeading);

	const groundPosition = cameraGroundPosition(pitch, heading, { lng: wrapLongitude(along_[0]), lat: along_[1] }, altitude);
	if (!groundPosition) return null;

	return { lng: groundPosition.lng, lat: groundPosition.lat, altitude, pitch, bearing: heading };
};

/**
 * Walks the whole planned route in coarse steps and returns the camera positions a warm-up pass
 * should visit, in film order: the intro's descent, then per leg a handful of points along it (about
 * one every `stepFrames`), then the stop it arrives at. Never throws -- a trip with degenerate
 * geometry should warm what it safely can rather than fail the render it is trying to speed up.
 *
 * Given `legStarts` (the absolute frame each leg starts on, by leg index -- the film's segment layout,
 * which this module cannot work out, it does not know the composition) and `introFrames`, every view
 * also says which frame of the film it is for: the intro's views are spread over the intro, leg view
 * `k` of `n` sits at `legStart + round(k / n * legFrames)`, and the stop a leg arrives at sits where
 * that stop's marker starts, which is where the leg ends. `keyframesInRange` then lets a tab that
 * renders only some of the frames walk only the views around them. A leg whose start is not a number
 * (the film does not draw it) leaves its views without a frame, and a view without a frame is never
 * left out.
 */
export const planWarmupKeyframes = (input: {
	reelData: any[];
	legPlans: LegPlan[];
	paths: any[];
	introStartAltitude: number;
	introEndAltitude: number;
	startPitch: number;
	stepFrames?: number;
	legStarts?: number[];
	introFrames?: number;
}): WarmupKeyframe[] => {
	const frames: WarmupKeyframe[] = [];
	const reelData = input?.reelData;
	if (!Array.isArray(reelData) || reelData.length === 0) return frames;

	const legPlans = input.legPlans;
	const paths = input.paths;
	const startPitch = input.startPitch;
	const stepFrames = input.stepFrames && input.stepFrames > 0 ? input.stepFrames : 15;
	const legStarts = input.legStarts;

	try {
		const firstPathCoords = paths?.[0]?.geometry?.coordinates;
		const introLng = reelData[0]?.lng ?? firstPathCoords?.[0]?.[0];
		const introLat = reelData[0]?.lat ?? firstPathCoords?.[0]?.[1];
		frames.push(...introKeyframes(introLng, introLat, input.introStartAltitude, input.introEndAltitude, startPitch, input.legStarts ? input.introFrames : undefined));
	} catch {
		// Nothing to fall back to for the intro; the per-leg loop below still runs.
	}

	for (let i = 0; i + 1 < reelData.length; i++) {
		const plan = legPlans?.[i];
		const pathFeature = paths?.[i];
		// Where this leg starts on the film's own clock, if the caller said.
		const legStart = legStarts?.[i];
		const hasStart = isFiniteNumber(legStart);

		if (plan && plan.frames > 0 && pathFeature?.geometry) {
			// Degenerate geometry (turf throws on it) should cost this leg its own keyframes, not the
			// whole warm-up -- so collect into a buffer and only keep it if the leg made it through clean.
			const legFrames: WarmupKeyframe[] = [];
			try {
				const routeDistance = length(pathFeature);
				const n = legWarmupSteps(plan, stepFrames);
				for (let k = 0; k <= n; k++) {
					const phase = k / n;
					const kf = legKeyframeAt(phase, reelData, i, plan, pathFeature, routeDistance, startPitch);
					pushIfValid(legFrames, kf && hasStart ? { ...kf, frame: legStart + Math.round(phase * plan.frames) } : kf);
				}
				frames.push(...legFrames);
			} catch {
				// Contribute nothing for this leg; the stop keyframe below is independent of it.
			}
		}

		// The stop this leg arrives at -- pushed whether or not the leg above contributed anything, so a
		// leg with no geometry or no frames still warms the place the camera parks at next.
		try {
			const lastPathCoord = paths?.[i]?.geometry?.coordinates?.slice(-1)?.[0];
			const stopLng = reelData[i + 1]?.lng ?? lastPathCoord?.[0];
			const stopLat = reelData[i + 1]?.lat ?? lastPathCoord?.[1];
			const bearing = legHeadingAt(reelData[i]?.path, 1);
			// Same offset MarkerSequence applies: the stop is the leading edge, the camera stands back.
			const ground = cameraGroundPosition(startPitch, bearing, { lng: stopLng, lat: stopLat }, plan?.exit);
			// The stop's marker starts where the leg ends -- or, for a leg the film does not draw, where the leg would have.
			const stopFrame = hasStart ? { frame: legStart + (plan?.frames > 0 ? plan.frames : 0) } : {};
			pushIfValid(frames, ground ? { lng: ground.lng, lat: ground.lat, altitude: plan?.exit, pitch: startPitch, bearing, ...stopFrame } : null);
		} catch {
			// No usable stop position; move on to the next leg.
		}
	}

	return frames;
};

/**
 * The views a tab that renders frames `from` to `to` (inclusive) needs from a warm-up plan.
 *
 * A chunk of a film rendered in its own tab flies over only part of the route, and walking all of it
 * in every tab fetched the same tiles once per tab. Keep the views from `margin` frames before the
 * first frame (the camera is moving towards it, and a view takes a few frames to settle) to the last
 * one, and the last view before that: the tab starts cold, and the ground it opens on is what that
 * view warms. A view with no frame is kept, because nothing says it can be left out. Order is the
 * film's, as the walk needs it.
 */
export const keyframesInRange = (keyframes: WarmupKeyframe[], [from, to]: [number, number], margin = 15): WarmupKeyframe[] => {
	const start = from - margin;
	let lastBefore = -1;
	keyframes.forEach((kf, i) => {
		if (isFiniteNumber(kf.frame) && kf.frame < start) lastBefore = i;
	});
	return keyframes.filter((kf, i) => !isFiniteNumber(kf.frame) || i === lastBefore || (kf.frame >= start && kf.frame <= to));
};

/**
 * Drives the map through `keyframes` in order, waiting for Mapbox to settle at each one before
 * moving to the next -- the same `areTilesLoaded`/`idle` check `TileGate` makes per frame, just made
 * once per coarse step instead of per frame. Resolves rather than rejects on every path, including a
 * keyframe that never reaches `idle`: a warm-up that gets partway there and stops is strictly better
 * than one that can fail the render it exists to speed up.
 */
export const warmTiles = (
	map: any,
	keyframes: WarmupKeyframe[],
	opts?: { keyframeTimeoutMs?: number; totalBudgetMs?: number }
): Promise<{ visited: number; timedOut: number; elapsedMs: number }> => {
	const keyframeTimeoutMs = opts?.keyframeTimeoutMs ?? 4000;
	const totalBudgetMs = opts?.totalBudgetMs ?? 90000;
	const started = Date.now();
	return new Promise((resolve) => {
		if (!map || !Array.isArray(keyframes) || keyframes.length === 0) {
			resolve({ visited: 0, timedOut: 0, elapsedMs: Date.now() - started });
			return;
		}
		const sources = tileSourceIds(map);
		const imports = hasImports(map);
		let visited = 0;
		let timedOut = 0;
		let index = 0;
		const visitNext = () => {
			if (index >= keyframes.length || Date.now() - started >= totalBudgetMs) {
				resolve({ visited, timedOut, elapsedMs: Date.now() - started });
				return;
			}
			const kf = keyframes[index];
			index += 1;
			try {
				// The exact sequence PathSequence and MarkerSequence use every frame: get, mutate, set.
				const camera = map.getFreeCameraOptions();
				camera.setPitchBearing(kf.pitch, kf.bearing);
				camera.position = MercatorCoordinate.fromLngLat({ lng: kf.lng, lat: kf.lat }, kf.altitude);
				map.setFreeCameraOptions(camera);
			} catch {
				// A camera call the map does not support should not stall the rest of the route.
				visited += 1;
				visitNext();
				return;
			}
			let settled = false;
			let raf = 0;
			const finish = (didTimeOut: boolean) => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				cancelAnimationFrame(raf);
				visited += 1;
				if (didTimeOut) timedOut += 1;
				visitNext();
			};
			const timer = setTimeout(() => finish(true), keyframeTimeoutMs);
			// Same check the per-frame gate makes, once per animation frame until the tile sources have
			// everything this view retains. Not `idle`: idle also waits for the GeoJSON re-tiling and
			// label placement this pass has no use for.
			const poll = () => {
				if (settled) return;
				if (tilesLoaded(map, sources, imports)) finish(false);
				else raf = requestAnimationFrame(poll);
			};
			raf = requestAnimationFrame(poll);
		};
		visitNext();
	});
};
